import { inject,Injectable } from "@angular/core";

import { DocxExportService } from "./docx-export.service";
import { MarkdownExportService } from "./markdown-export.service";
import { PdfExportService } from "./pdf-export.service";
import { TextExportService } from "./text-export.service";

export interface ExportOptions {
  title?: string;
  includeStyles?: boolean;
}

@Injectable({
  providedIn: "root"
})
export class DocumentExportService {
  private readonly _pdfExportService = inject(PdfExportService);
  private readonly _docxExportService = inject(DocxExportService);
  private readonly _markdownExportService = inject(MarkdownExportService);
  private readonly _textExportService = inject(TextExportService);

  /**
   * Export document as DOCX format
   */
  async exportToDocx(markdown: string, title: string = "Document"): Promise<Blob> {
    return this._docxExportService.exportToDocx(markdown, title);
  }

  /**
   * Export document as PDF format
   */
  async exportToPdf(title: string, markdown: string): Promise<Blob> {
    return this._pdfExportService.exportToPdf(title, markdown);
  }

  /**
   * Export document as plain text
   */
  exportToText(markdown: string): string {
    return this._textExportService.exportToText(markdown);
  }

  /**
   * Export document as markdown
   */
  exportToMarkdown(markdown: string): string {
    return this._markdownExportService.exportToMarkdown(markdown);
  }

  /**
   * Normalize and clean markdown content
   */
  normalizeMarkdown(markdown: string): string {
    return this._markdownExportService.normalizeMarkdown(markdown);
  }

  /**
   * Copy text with formatting to clipboard
   */
  async copyTextWithFormatting(markdown: string): Promise<void> {
    return this._textExportService.copyTextWithFormatting(markdown);
  }

  /**
   * Copy text to clipboard
   */
  async copyToClipboard(text: string): Promise<void> {
    return this._textExportService.copyToClipboard(text);
  }

  /**
   * Export document as HTML
   */
  exportToHtml(markdown: string, title: string = "Document"): string {
    return this._pdfExportService.exportToHtml(markdown, title);
  }
}
